import {
  ACHIEVEMENTS,
  BOSSES,
  CHALLENGE_MODIFIERS,
  CLASSES,
  COMPANIONS,
  ENEMIES,
  FIXED_STEP,
  QUESTS,
  RARITIES,
  REGIONS,
  SAVE_SCHEMA_VERSION,
  SKILL_TREES,
  STATUS_DEFINITIONS,
  VENDORS,
} from "./config.js";
import {
  IdFactory,
  SeededRandom,
  activateCheckpoint,
  applyCrafting,
  canUseAbility,
  buyVendorItem,
  completeQuest,
  createCharacter,
  createRuntime,
  damageEnemy,
  derivedStats,
  distance,
  dodge,
  equipItem,
  executeAbility,
  fixedUpdate,
  generateItem,
  grantExperience,
  pickupItem,
  playerDeath,
  progressQuest,
  recoverEcho,
  recruitCompanion,
  respawn,
  salvageItem,
  sellInventoryItem,
  serializeRuntime,
  spawnEnemy,
  spawnZonePopulation,
  spendSkillPoint,
  transferItem,
  unequipItem,
  usePotion,
  unlockAchievement,
  validateCharacterName,
  validateItemOwnership,
} from "./core.js";
import {
  NavigationGrid,
  challengeDefinition,
  dungeonWalkableCells,
  generateDungeon,
  validateDungeon,
} from "./dungeon.js";
import { AudioManager } from "./audio.js";
import { SaveManager } from "./persistence.js";
import { ThreeRenderer, drawMinimap } from "./renderer.js";

const select = (root, selector) => root.querySelector(selector);
const selectAll = (root, selector) => [...root.querySelectorAll(selector)];
const clear = (element) => element.replaceChildren();
function element(tag, className = "", text = "") {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}
function button(text, onClick, className = "") {
  const node = element("button", className, text);
  node.type = "button";
  node.addEventListener("click", onClick);
  return node;
}
function line(label, value) {
  const row = element("div", "rf-stat-line");
  row.append(element("span", "", label), element("strong", "", String(value)));
  return row;
}

class InputController {
  constructor(root, canvas, renderer, game, signal) {
    this.root = root;
    this.canvas = canvas;
    this.renderer = renderer;
    this.game = game;
    this.keys = new Set();
    this.joystick = { x: 0, z: 0 };
    this.pointerWorld = { x: 0, z: 1 };
    this.pointerInside = false;
    this.joystickPointer = null;
    this.virtualBlock = false;
    this.pinchDistance = null;
    this.touches = new Map();
    this.controller = new AbortController();
    this.signal = this.controller.signal;
    if (signal.aborted) this.controller.abort();
    else
      signal.addEventListener("abort", () => this.controller.abort(), {
        once: true,
      });
    this.install();
  }
  ignored(event) {
    return (
      ["INPUT", "SELECT", "TEXTAREA", "BUTTON"].includes(
        event.target?.tagName,
      ) || event.target?.isContentEditable
    );
  }
  install() {
    const options = { signal: this.signal };
    window.addEventListener(
      "keydown",
      (event) => {
        if (this.ignored(event) || !this.game.runtime) return;
        const key = event.key.toLowerCase();
        if (this.game.runtime.paused && key !== "escape") return;
        if (
          [
            "w",
            "a",
            "s",
            "d",
            "arrowup",
            "arrowdown",
            "arrowleft",
            "arrowright",
            "shift",
          ].includes(key)
        ) {
          event.preventDefault();
          this.keys.add(key);
          this.game.cancelPath();
        }
        const panels = {
          i: "inventory",
          c: "character",
          k: "skills",
          j: "quests",
          m: "map",
        };
        if (panels[key]) {
          event.preventDefault();
          this.game.openPanel(panels[key]);
        }
        if (key === "escape") {
          event.preventDefault();
          this.game.escape();
        }
        if (key === "q") {
          event.preventDefault();
          this.game.drinkPotion();
        }
        if (key === "e") {
          event.preventDefault();
          this.game.interact();
        }
        if (key === " ") {
          event.preventDefault();
          this.game.dodge();
        }
        if (key === "tab") {
          event.preventDefault();
          this.game.cycleTarget();
        }
        if (/^[1-5]$/.test(key)) {
          event.preventDefault();
          this.game.useAbility(Number(key) - 1);
        }
      },
      options,
    );
    window.addEventListener(
      "keyup",
      (event) => this.keys.delete(event.key.toLowerCase()),
      options,
    );
    window.addEventListener("blur", () => this.clear(), options);
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.hidden) {
          this.clear();
          this.game.pause("Page hidden");
        }
      },
      options,
    );
    this.canvas.addEventListener(
      "pointerenter",
      () => {
        this.pointerInside = true;
      },
      options,
    );
    this.canvas.addEventListener(
      "pointerleave",
      () => {
        this.pointerInside = false;
      },
      options,
    );
    this.canvas.addEventListener(
      "pointermove",
      (event) => {
        const world = this.renderer.screenToWorld(event.clientX, event.clientY);
        if (world) this.pointerWorld = world;
      },
      options,
    );
    this.canvas.addEventListener(
      "pointerdown",
      (event) => {
        if (event.pointerType === "touch") {
          this.touches.set(event.pointerId, {
            x: event.clientX,
            y: event.clientY,
          });
          return;
        }
        if (event.button !== 0 && event.button !== 2) return;
        event.preventDefault();
        this.game.audio?.unlock();
        const enemy = this.renderer.enemyAt(
          event.clientX,
          event.clientY,
          this.game.runtime,
        );
        if (event.button === 2)
          this.game.useAbility(1, enemy, this.pointerWorld);
        else if (enemy) {
          this.game.target = enemy;
          this.game.basicAttack(enemy);
        } else this.game.setPath(this.pointerWorld);
      },
      options,
    );
    this.canvas.addEventListener(
      "contextmenu",
      (event) => event.preventDefault(),
      options,
    );
    this.canvas.addEventListener(
      "wheel",
      (event) => {
        if (!this.pointerInside) return;
        event.preventDefault();
        this.renderer.zoom(Math.sign(event.deltaY) * 1.5);
      },
      { ...options, passive: false },
    );
    this.canvas.addEventListener(
      "pointermove",
      (event) => {
        if (!this.touches.has(event.pointerId)) return;
        const previous = this.touches.get(event.pointerId);
        this.touches.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
        });
        if (this.touches.size === 2) {
          const [a, b] = [...this.touches.values()];
          const current = Math.hypot(a.x - b.x, a.y - b.y);
          if (this.pinchDistance)
            this.renderer.zoom((this.pinchDistance - current) * 0.03);
          this.pinchDistance = current;
        } else if (Math.abs(event.clientX - previous.x) > 4)
          this.renderer.rotate((event.clientX - previous.x) * -0.003);
      },
      options,
    );
    const endTouch = (event) => {
      if (this.touches.size === 1 && this.touches.has(event.pointerId)) {
        const world = this.renderer.screenToWorld(event.clientX, event.clientY);
        if (world) {
          const enemy = this.renderer.enemyAt(
            event.clientX,
            event.clientY,
            this.game.runtime,
          );
          enemy ? this.game.basicAttack(enemy) : this.game.setPath(world);
        }
      }
      this.touches.delete(event.pointerId);
      this.pinchDistance = null;
    };
    this.canvas.addEventListener("pointerup", endTouch, options);
    this.canvas.addEventListener("pointercancel", endTouch, options);
    const joystick = select(this.root, "[data-rf-joystick]");
    const knob = select(this.root, "[data-rf-joystick-knob]");
    const updateJoystick = (event) => {
      const rect = joystick.getBoundingClientRect();
      const x = event.clientX - (rect.left + rect.width / 2);
      const y = event.clientY - (rect.top + rect.height / 2);
      const length = Math.hypot(x, y);
      const scale = length > 40 ? 40 / length : 1;
      const nx = x * scale,
        ny = y * scale;
      knob.style.transform = `translate(calc(-50% + ${nx}px), calc(-50% + ${ny}px))`;
      this.joystick = { x: nx / 40, z: ny / 40 };
      this.game.cancelPath();
    };
    joystick.addEventListener(
      "pointerdown",
      (event) => {
        event.preventDefault();
        this.joystickPointer = event.pointerId;
        joystick.setPointerCapture(event.pointerId);
        updateJoystick(event);
      },
      options,
    );
    joystick.addEventListener(
      "pointermove",
      (event) => {
        if (event.pointerId === this.joystickPointer) updateJoystick(event);
      },
      options,
    );
    const releaseJoystick = (event) => {
      if (event.pointerId !== this.joystickPointer) return;
      this.joystickPointer = null;
      this.joystick = { x: 0, z: 0 };
      knob.style.transform = "translate(-50%, -50%)";
    };
    joystick.addEventListener("pointerup", releaseJoystick, options);
    joystick.addEventListener("pointercancel", releaseJoystick, options);
    const block = select(this.root, "[data-rf-block]");
    const releaseBlock = () => {
      this.virtualBlock = false;
      block.setAttribute("aria-pressed", "false");
    };
    block.setAttribute("aria-pressed", "false");
    block.addEventListener(
      "pointerdown",
      (event) => {
        event.preventDefault();
        this.virtualBlock = true;
        block.setAttribute("aria-pressed", "true");
      },
      options,
    );
    block.addEventListener("pointerup", releaseBlock, options);
    block.addEventListener("pointercancel", releaseBlock, options);
    block.addEventListener("pointerleave", releaseBlock, options);
  }
  vector() {
    let x = this.joystick.x,
      z = this.joystick.z;
    if (this.keys.has("a") || this.keys.has("arrowleft")) x -= 1;
    if (this.keys.has("d") || this.keys.has("arrowright")) x += 1;
    if (this.keys.has("w") || this.keys.has("arrowup")) z -= 1;
    if (this.keys.has("s") || this.keys.has("arrowdown")) z += 1;
    const length = Math.hypot(x, z);
    if (length > 1) {
      x /= length;
      z /= length;
    }
    const angle = this.renderer.cameraAngle;
    const right = { x: -Math.sin(angle), z: Math.cos(angle) };
    const forward = { x: -Math.cos(angle), z: -Math.sin(angle) };
    return {
      x: right.x * x + forward.x * -z,
      z: right.z * x + forward.z * -z,
      block: this.keys.has("shift") || this.virtualBlock,
    };
  }
  clear() {
    this.keys.clear();
    this.joystick = { x: 0, z: 0 };
    this.virtualBlock = false;
    const knob = select(this.root, "[data-rf-joystick-knob]");
    if (knob) knob.style.transform = "translate(-50%, -50%)";
    select(this.root, "[data-rf-block]")?.setAttribute("aria-pressed", "false");
  }
  teardown() {
    this.controller.abort();
    this.clear();
    this.touches.clear();
  }
}

class RealmfallGame {
  constructor(root) {
    this.root = root;
    this.controller = new AbortController();
    this.saveManager = new SaveManager();
    this.renderer = null;
    this.audio = null;
    this.input = null;
    this.runtime = null;
    this.slotIndex = null;
    this.mode = "campaign";
    this.idFactory = new IdFactory("realmfall");
    this.rng = new SeededRandom("realmfall");
    this.animationFrame = null;
    this.lastTime = 0;
    this.running = false;
    this.target = null;
    this.navigation = null;
    this.autosaveRemaining = 12;
    this.sessionVersion = 0;
    this.toastTimers = new Set();
    this.announceFrame = null;
    this.debug =
      new URLSearchParams(location.search).get("realmfall_debug") === "1";
    this.elements = Object.fromEntries(
      [
        "launcher",
        "stage",
        "canvas",
        "minimap",
        "slot-list",
        "creator",
        "creator-form",
        "class-grid",
        "preview-canvas",
        "class-motto",
        "name-error",
        "game-panel",
        "panel-title",
        "panel-content",
        "dialogue",
        "pause-menu",
        "death",
        "diagnostics",
        "debug-output",
        "toasts",
        "live",
        "save-state",
        "context-loss",
        "ability-bar",
        "health",
        "health-fill",
        "resource",
        "resource-fill",
        "resource-name",
        "xp",
        "xp-fill",
        "player-name",
        "player-class",
        "zone",
        "chapter",
        "objective",
        "quest-title",
        "quest-objective",
        "quest-progress",
        "target-card",
        "target-name",
        "target-rank",
        "target-health",
        "boss-hud",
        "boss-name",
        "boss-title",
        "boss-health",
        "boss-phase",
        "statuses",
        "interact",
        "loot-layer",
      ].map((name) => [name, select(root, `[data-rf-${name}]`)]),
    );
    this.selectedClass = "barbarian";
    this.resizeObserver = null;
    this.manifest = null;
    this.installUI();
  }
  async initialize() {
    const version = this.sessionVersion;
    this.renderClassChoices();
    this.renderSlots();
    this.elements.diagnostics.hidden = !this.debug;
    await this.saveManager.initialize();
    if (this.controller.signal.aborted || version !== this.sessionVersion)
      return;
    this.applySettings();
    await this.loadManifest();
    this.renderSlots();
    this.announce("Realmfall profile ready.");
  }
  async loadManifest() {
    const version = this.sessionVersion;
    try {
      const response = await fetch(this.root.dataset.manifest, {
        credentials: "same-origin",
        signal: this.controller.signal,
      });
      if (!response.ok) throw new Error("manifest");
      this.manifest = await response.json();
      if (this.controller.signal.aborted || version !== this.sessionVersion)
        this.manifest = null;
    } catch (_error) {
      if (this.controller.signal.aborted || version !== this.sessionVersion)
        return;
      this.toast("Some optional artwork could not be loaded.", "#e9c96f");
    }
  }
  installUI() {
    const signal = this.controller.signal;
    selectAll(this.root, "[data-rf-mode]").forEach((node) =>
      node.addEventListener(
        "click",
        () => this.selectMode(node.dataset.rfMode),
        { signal },
      ),
    );
    select(this.root, "[data-rf-new-character]").addEventListener(
      "click",
      () => this.openCreator(),
      { signal },
    );
    select(this.root, "[data-rf-randomize]").addEventListener(
      "click",
      () => this.randomizeCreator(),
      { signal },
    );
    this.elements["creator-form"].addEventListener(
      "submit",
      (event) => {
        event.preventDefault();
        this.submitCreator(new FormData(event.currentTarget));
      },
      { signal },
    );
    select(this.root, "[data-rf-import]").addEventListener(
      "click",
      () => select(this.root, "[data-rf-import-input]").click(),
      { signal },
    );
    select(this.root, "[data-rf-import-input]").addEventListener(
      "change",
      (event) => this.importSave(event.target.files?.[0]),
      { signal },
    );
    select(this.root, "[data-rf-audio]").addEventListener(
      "click",
      (event) => {
        const enabled =
          event.currentTarget.getAttribute("aria-pressed") !== "true";
        event.currentTarget.setAttribute("aria-pressed", String(enabled));
        const settings = {
          ...this.saveManager.settings,
          masterVolume: enabled ? 0.7 : 0,
        };
        this.saveManager.saveSettings(settings);
        this.audio?.applySettings(settings);
      },
      { signal },
    );
    select(this.root, "[data-rf-fullscreen]").addEventListener(
      "click",
      () => this.toggleFullscreen(),
      { signal },
    );
    selectAll(this.root, "[data-rf-panel]").forEach((node) =>
      node.addEventListener(
        "click",
        () => this.openPanel(node.dataset.rfPanel),
        { signal },
      ),
    );
    selectAll(this.root, "[data-rf-panel-tab]").forEach((node) =>
      node.addEventListener(
        "click",
        () => this.renderPanel(node.dataset.rfPanelTab),
        { signal },
      ),
    );
    select(this.root, "[data-rf-close-panel]").addEventListener(
      "click",
      () => this.closePanel(),
      { signal },
    );
    select(this.root, "[data-rf-pause]").addEventListener(
      "click",
      () => this.pause(),
      { signal },
    );
    select(this.root, "[data-rf-resume]").addEventListener(
      "click",
      () => this.resume(),
      { signal },
    );
    select(this.root, "[data-rf-force-save]").addEventListener(
      "click",
      () => this.save(),
      { signal },
    );
    select(this.root, "[data-rf-return-title]").addEventListener(
      "click",
      () => this.returnToTitle(),
      { signal },
    );
    select(this.root, "[data-rf-respawn]").addEventListener(
      "click",
      () => this.handleRespawn(),
      { signal },
    );
    select(this.root, "[data-rf-dodge]").addEventListener(
      "click",
      () => this.dodge(),
      { signal },
    );
    select(this.root, "[data-rf-potion]").addEventListener(
      "click",
      () => this.drinkPotion(),
      { signal },
    );
    select(this.root, "[data-rf-touch-interact]").addEventListener(
      "click",
      () => this.interact(),
      { signal },
    );
    select(this.root, "[data-rf-basic-attack]").addEventListener(
      "click",
      () => this.basicAttack(this.nearestEnemy()),
      { signal },
    );
    select(this.root, "[data-rf-debug-close]").addEventListener(
      "click",
      () => {
        this.elements.diagnostics.hidden = true;
      },
      { signal },
    );
    selectAll(this.root, "[data-rf-debug]").forEach((node) =>
      node.addEventListener(
        "click",
        () => this.debugAction(node.dataset.rfDebug),
        { signal },
      ),
    );
    window.addEventListener("pagehide", () => this.teardown(), { signal });
    window.addEventListener(
      "beforeunload",
      () => {
        if (this.runtime && this.slotIndex !== null) this.save();
      },
      { signal },
    );
  }
  selectMode(mode) {
    if (
      mode === "challenge" &&
      !this.saveManager.slots.some((slot) =>
        slot?.character?.achievements?.includes("campaign"),
      )
    ) {
      this.toast("Challenge Dungeons unlock after Chapter V.", "#e9c96f");
      return;
    }
    this.mode = mode;
    selectAll(this.root, "[data-rf-mode]").forEach((node) =>
      node.setAttribute("aria-pressed", String(node.dataset.rfMode === mode)),
    );
    this.audio?.play("click");
  }
  renderClassChoices() {
    clear(this.elements["class-grid"]);
    for (const klass of Object.values(CLASSES)) {
      const node = element("button", "rf-class-option");
      node.type = "button";
      node.style.setProperty("--class-color", klass.color);
      node.setAttribute(
        "aria-pressed",
        String(klass.id === this.selectedClass),
      );
      node.append(
        element("strong", "", klass.name),
        element("small", "", `${klass.resource} · ${klass.preferences[0]}`),
      );
      node.addEventListener("click", () => {
        this.selectedClass = klass.id;
        this.renderClassChoices();
        this.elements["class-motto"].textContent = klass.motto;
        this.drawPreview();
      });
      this.elements["class-grid"].append(node);
    }
  }
  drawPreview() {
    const canvas = this.elements["preview-canvas"],
      context = canvas.getContext("2d"),
      form = this.elements["creator-form"].elements,
      klass = CLASSES[this.selectedClass];
    context.clearRect(0, 0, canvas.width, canvas.height);
    const gradient = context.createRadialGradient(210, 220, 20, 210, 220, 190);
    gradient.addColorStop(0, `${klass.color}66`);
    gradient.addColorStop(1, "transparent");
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.save();
    context.translate(210, 65);
    const skin = form.skin?.value || "#d9a978",
      accent = form.accent?.value || klass.color;
    context.fillStyle = "rgba(0,0,0,.3)";
    context.beginPath();
    context.ellipse(0, 405, 85, 18, 0, 0, Math.PI * 2);
    context.fill();
    context.strokeStyle = "#20302f";
    context.lineWidth = 30;
    context.lineCap = "round";
    context.beginPath();
    context.moveTo(-30, 260);
    context.lineTo(-42, 380);
    context.moveTo(30, 260);
    context.lineTo(42, 380);
    context.stroke();
    context.strokeStyle = accent;
    context.lineWidth = 28;
    context.beginPath();
    context.moveTo(-58, 150);
    context.lineTo(-82, 275);
    context.moveTo(58, 150);
    context.lineTo(82, 275);
    context.stroke();
    context.fillStyle = accent;
    context.beginPath();
    context.moveTo(-70, 120);
    context.lineTo(70, 120);
    context.lineTo(58, 285);
    context.lineTo(-58, 285);
    context.closePath();
    context.fill();
    context.fillStyle = skin;
    context.beginPath();
    context.arc(0, 75, 48, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = form.hairColor?.value || "#402d25";
    context.beginPath();
    context.arc(0, 63, 49, Math.PI, Math.PI * 2);
    context.fill();
    context.fillStyle = "#bfc8c5";
    if (klass.id === "barbarian") {
      context.fillRect(75, 165, 12, 210);
      context.beginPath();
      context.moveTo(87, 190);
      context.lineTo(145, 215);
      context.lineTo(87, 240);
      context.fill();
    } else if (klass.id === "ranger") {
      context.strokeStyle = klass.color;
      context.lineWidth = 8;
      context.beginPath();
      context.arc(92, 220, 50, -Math.PI / 2, Math.PI / 2);
      context.stroke();
    } else {
      context.fillRect(78, 175, 10, 190);
      context.fillStyle = klass.color;
      context.beginPath();
      context.arc(83, 166, 18, 0, Math.PI * 2);
      context.fill();
    }
    context.restore();
  }
  randomizeCreator() {
    const form = this.elements["creator-form"].elements,
      names = [
        "Aster",
        "Mirel",
        "Torren",
        "Nyra",
        "Cael",
        "Veya",
        "Orin",
        "Sable",
      ];
    form.name.value = names[Math.floor(Math.random() * names.length)];
    const ids = Object.keys(CLASSES);
    this.selectedClass = ids[Math.floor(Math.random() * ids.length)];
    form.accent.value = CLASSES[this.selectedClass].color;
    this.renderClassChoices();
    this.drawPreview();
  }
  openCreator() {
    if (this.saveManager.firstFreeSlot() < 0) {
      this.toast("All three character slots are occupied.", "#e95f66");
      return;
    }
    this.elements["name-error"].textContent = "";
    this.elements.creator.showModal();
    this.drawPreview();
  }
  submitCreator(data) {
    const name = validateCharacterName(data.get("name"));
    if (!name.valid) {
      this.elements["name-error"].textContent = name.error;
      return;
    }
    try {
      const character = createCharacter(
        {
          name: name.name,
          classId: this.selectedClass,
          mode: this.mode,
          body: data.get("body"),
          skin: data.get("skin"),
          hair: data.get("hair"),
          hairColor: data.get("hairColor"),
          accent: data.get("accent"),
          tone: data.get("tone"),
          helmet: data.get("helmet") === "on",
          cape: data.get("cape") === "on",
        },
        this.idFactory,
      );
      this.slotIndex = this.saveManager.firstFreeSlot();
      this.elements.creator.close();
      this.startRuntime(
        createRuntime(character, `${Date.now()}-${character.id}`),
        false,
      );
      this.toast(`Welcome to the Wardens, ${character.name}.`);
      this.announce(
        `Character created. ${character.name}, ${CLASSES[character.classId].name}.`,
      );
    } catch (error) {
      this.elements["name-error"].textContent = error.message;
    }
  }
  renderSlots() {
    clear(this.elements["slot-list"]);
    this.saveManager.slots.forEach((slot, index) => {
      if (!slot) {
        const empty = element("div", "rf-slot");
        empty.append(
          element("span", "rf-slot-portrait", "+"),
          element("div", "", "Empty character slot"),
        );
        this.elements["slot-list"].append(empty);
        return;
      }
      const character = slot.character,
        row = element("div", "rf-slot");
      const portrait = element(
        "span",
        "rf-slot-portrait",
        CLASSES[character.classId].name[0],
      );
      portrait.style.setProperty(
        "--slot-color",
        CLASSES[character.classId].color,
      );
      const info = element("div");
      info.append(
        element("strong", "", character.name),
        element(
          "small",
          "",
          `${CLASSES[character.classId].name} · Level ${character.level} · ${REGIONS.find((r) => r.id === character.currentZone)?.name ?? "Asterra"}`,
        ),
      );
      const actions = element("div", "rf-slot-actions");
      actions.append(
        button("Play", () => this.loadSlot(index)),
        button("Export", () => this.exportSave(index)),
        button("Delete", () => this.deleteSlot(index)),
      );
      row.append(portrait, info, actions);
      this.elements["slot-list"].append(row);
    });
  }
  async loadSlot(index) {
    try {
      this.slotIndex = index;
      this.startRuntime(this.saveManager.load(index), true);
      this.toast("Save restored in a paused state.");
    } catch (error) {
      this.toast(error.message, "#e95f66");
    }
  }
  async deleteSlot(index) {
    if (!confirm("Delete this Realmfall character? This cannot be undone."))
      return;
    await this.saveManager.delete(index);
    this.renderSlots();
  }
  exportSave(index) {
    try {
      const blob = new Blob([this.saveManager.export(index)], {
        type: "application/json",
      });
      const anchor = document.createElement("a");
      anchor.href = URL.createObjectURL(blob);
      anchor.download = `realmfall-${this.saveManager.slots[index].character.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.json`;
      anchor.click();
      URL.revokeObjectURL(anchor.href);
    } catch (error) {
      this.toast(error.message, "#e95f66");
    }
  }
  async importSave(file) {
    if (!file) return;
    try {
      const index = await this.saveManager.import(await file.text());
      this.renderSlots();
      this.toast(`Save imported into slot ${index + 1}.`);
    } catch (error) {
      this.toast(error.message, "#e95f66");
    }
  }
  startRuntime(runtime, restored) {
    this.sessionVersion += 1;
    this.runtime = runtime;
    runtime.tokens ??= {};
    runtime.tokens.session = `session-${this.sessionVersion}`;
    if (runtime.mode === "challenge") {
      runtime.dungeon ??= generateDungeon(
        `${runtime.seed}:tier:${runtime.character.challengeTier}`,
      );
      runtime.challenge ??= challengeDefinition(
        runtime.dungeon.seed,
        runtime.character.challengeTier,
      );
      runtime.challengeElapsed ??= 0;
      runtime.challengeComplete ??= false;
    }
    this.rng = new SeededRandom(runtime.seed);
    this.idFactory = new IdFactory(
      runtime.generationToken,
      runtime.idCounter ?? runtime.actionCounter,
    );
    this.elements.launcher.hidden = true;
    this.elements.stage.hidden = false;
    if (!this.renderer) {
      try {
        this.renderer = new ThreeRenderer(
          this.elements.canvas,
          this.saveManager.settings,
          this.root.dataset.assets,
        );
        this.renderer.onContextLost = () => {
          this.elements["context-loss"].hidden = false;
          this.pause("WebGL context lost");
          this.save();
        };
        this.renderer.onContextRestored = () => {
          this.elements["context-loss"].hidden = true;
          this.renderer.buildWorld(this.runtime.zoneId, this.runtime.dungeon);
        };
      } catch (_error) {
        this.elements.stage.hidden = true;
        this.elements.launcher.hidden = false;
        this.toast("WebGL could not start on this device.", "#e95f66");
        return;
      }
    }
    if (!this.audio) this.audio = new AudioManager(this.saveManager.settings);
    if (!this.input)
      this.input = new InputController(
        this.root,
        this.elements.canvas,
        this.renderer,
        this,
        this.controller.signal,
      );
    this.renderer.setPlayer(runtime.character);
    this.setupZone(restored);
    this.renderAbilityBar();
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.renderer?.resize());
    this.resizeObserver.observe(this.elements.canvas);
    this.running = true;
    this.lastTime = performance.now();
    if (this.animationFrame === null)
      this.animationFrame = requestAnimationFrame((time) => this.loop(time));
    this.updateHUD();
    this.audio.setMusic(
      runtime.zoneId === "veilwatch"
        ? "hub"
        : (REGIONS.find((r) => r.id === runtime.zoneId)?.terrain ?? "grass"),
    );
    if (restored) {
      runtime.paused = true;
      runtime.gameState = "paused";
      this.elements["pause-menu"].showModal();
      this.audio.setPaused(true);
    } else {
      runtime.paused = false;
      runtime.gameState = "playing";
      this.audio.setPaused(false);
      this.showIntroDialogue();
      this.save();
    }
  }
  setupZone(preserveEntities = false) {
    const runtime = this.runtime;
    if (!preserveEntities || !runtime.zoneInitialized) {
      runtime.enemies = [];
      runtime.projectiles = [];
      runtime.effects = [];
      runtime.summons = [];
      runtime.interactables = [];
      for (const item of runtime.groundItems ?? []) item.owner = "destroyed";
      runtime.groundItems = [];
      runtime.corpses = [];
      runtime.boss = null;
      if (runtime.zoneId === "veilwatch") this.setupHub();
      else {
        if (runtime.dungeon) this.setupDungeonPopulation();
        else {
          spawnZonePopulation(runtime, this.rng, this.idFactory);
          this.setupQuestInteractables();
        }
        if (runtime.challenge) {
          for (const enemy of runtime.enemies) {
            enemy.maxHealth = Math.round(
              enemy.maxHealth * runtime.challenge.enemyScale,
            );
            enemy.health = enemy.maxHealth;
            enemy.damage *= runtime.challenge.enemyScale;
            if (!enemy.elite && this.rng.next() < runtime.challenge.eliteChance)
              enemy.elite = {
                id: "challenge-crowned",
                name: "Challenge-Crowned",
                color: "#f1c96c",
              };
          }
        }
        this.spawnRegionBoss();
      }
      runtime.zoneInitialized = true;
    }
    this.buildNavigation();
    if (runtime.character.activeCompanion) {
      const definition = COMPANIONS.find(
        (c) => c.id === runtime.character.activeCompanion,
      );
      if (definition)
        runtime.companion = {
          id: `companion-${definition.id}`,
          definitionId: definition.id,
          position: {
            x: runtime.player.position.x - 2,
            z: runtime.player.position.z + 1,
          },
          health: 100,
          maxHealth: 100,
          alive: true,
          cooldown: 0,
          state: "follow",
          zoneId: runtime.zoneId,
          generationToken: runtime.tokens?.zone ?? 1,
        };
    }
  }
  setupHub() {
    const entries = [
      ["ilyra", "npc", "Warden Ilyra", 0, -2],
      ["maelin", "vendor", "Maelin's Provisions", -7, -2],
      ["brannoc", "crafting", "Brannoc Ironhand", 7, -2],
      ["nariel", "enchanter", "Nariel Runeweaver", -12, 5],
      ["stash", "stash", "Warden Stash", 12, 5],
      ["waystone", "checkpoint", "Sanctuary Waystone", 0, 8],
      ["portal", "portal", "March Portal", 0, -8],
      ["serah", "companion", "Serah, Veil Scout", -9, 10],
      ["doran", "companion", "Doran, Exiled Shieldbearer", 9, 10],
      ["elys", "companion", "Elys, Runebound Scholar", 0, 14],
    ];
    this.runtime.interactables = entries.map(([id, type, name, x, z]) => ({
      id,
      type,
      name,
      position: { x, z },
      active: type === "checkpoint",
      destination: type === "portal" ? "verdant-march" : null,
    }));
  }
  setupQuestInteractables() {
    const quest = QUESTS.find(
      (entry) => entry.id === `chapter-${this.runtime.character.chapter}`,
    );
    if (!quest) return;
    const positions = [
      [-5, 5],
      [6, 7],
      [-9, -5],
      [10, -7],
    ];
    for (const objective of quest.objectives) {
      if (["defeat", "boss"].includes(objective.type)) continue;
      const count = Math.min(4, objective.target);
      for (let index = 0; index < count; index += 1) {
        const [x, z] = positions[index];
        this.runtime.interactables.push({
          id: `${quest.id}-${objective.type}-${index}`,
          type: "objective",
          objectiveType: objective.type,
          name:
            objective.type === "choice"
              ? "Crown Covenant"
              : objective.text.replace(
                  /^(Recover|Awaken|Chart|Align|Guide|Ring|Set)\s+/i,
                  "",
                ),
          position: { x: x + quest.chapter, z: z - quest.chapter },
          used: false,
        });
      }
    }
  }
  dungeonWorld(point) {
    return {
      x: point.x - this.runtime.dungeon.width / 2 + 0.5,
      z: point.y - this.runtime.dungeon.height / 2 + 0.5,
    };
  }
  setupDungeonPopulation() {
    const dungeon = this.runtime.dungeon;
    const region = REGIONS.find((entry) => entry.id === this.runtime.zoneId);
    const pool = ENEMIES.filter((entry) =>
      region.factions.includes(entry.faction),
    );
    if (this.runtime.time === 0) {
      const entrance = this.dungeonWorld(dungeon.entrance);
      this.runtime.player.position = { ...entrance, y: 0 };
      this.runtime.character.position = { ...this.runtime.player.position };
    }
    this.runtime.dungeonState ??= {
      keys: [],
      unlockedDoors: [],
      discoveredRooms: [],
      openedTreasure: [],
    };
    for (const spawn of dungeon.spawns) {
      spawnEnemy(
        this.runtime,
        this.rng.pick(pool).id,
        this.dungeonWorld(spawn),
        {
          elite:
            this.rng.next() < (this.runtime.challenge?.eliteChance ?? 0.12),
        },
        this.idFactory,
      );
    }
    this.runtime.interactables.push(
      {
        id: "dungeon-checkpoint",
        type: "checkpoint",
        name: "Challenge Waystone",
        position: this.dungeonWorld(dungeon.checkpoint),
        active: false,
      },
      {
        id: dungeon.key.id,
        type: "key",
        name: "Sigil Key",
        position: this.dungeonWorld(dungeon.key),
        used: this.runtime.dungeonState.keys.includes(dungeon.key.id),
      },
      {
        id: dungeon.lock.id,
        type: "door",
        keyId: dungeon.lock.keyId,
        name: "Sigil Door",
        position: this.dungeonWorld(dungeon.lock),
        used: this.runtime.dungeonState.unlockedDoors.includes(dungeon.lock.id),
      },
      {
        id: "dungeon-exit",
        type: "portal",
        name: "Veilwatch Return Gate",
        position: this.dungeonWorld(dungeon.exit),
        destination: "veilwatch",
      },
    );
  }
  spawnRegionBoss() {
    const boss = BOSSES.find((entry) => entry.zone === this.runtime.zoneId);
    if (!boss) return;
    if (
      !this.runtime.challenge &&
      this.runtime.character.defeatedBosses?.includes(boss.id)
    )
      return;
    const region = REGIONS.find((entry) => entry.id === this.runtime.zoneId);
    const pool = ENEMIES.filter((entry) =>
      region.factions.includes(entry.faction),
    );
    const base =
      pool.find((entry) => ["tank", "artillery"].includes(entry.role)) ??
      pool[0];
    const position = this.runtime.dungeon
      ? this.dungeonWorld(this.runtime.dungeon.boss)
      : { x: 0, z: -25 };
    const entity = spawnEnemy(
      this.runtime,
      base.id,
      position,
      {
        boss: true,
        bossId: boss.id,
        name: boss.name,
        challenge: Boolean(this.runtime.challenge),
      },
      this.idFactory,
    );
    if (this.runtime.challenge) {
      entity.maxHealth = Math.round(
        entity.maxHealth * this.runtime.challenge.enemyScale,
      );
      entity.health = entity.maxHealth;
      entity.damage *= this.runtime.challenge.enemyScale;
    }
  }
  buildNavigation() {
    const size = 64,
      cells = [];
    if (this.runtime.dungeon) {
      const offsetX = Math.floor((size - this.runtime.dungeon.width) / 2);
      const offsetY = Math.floor((size - this.runtime.dungeon.height) / 2);
      const unlocked = this.runtime.dungeonState?.unlockedDoors?.includes(
        this.runtime.dungeon.lock.id,
      );
      for (const cell of dungeonWalkableCells(this.runtime.dungeon, unlocked))
        cells.push({ x: cell.x + offsetX, y: cell.y + offsetY });
      this.navigation = new NavigationGrid(size, size, cells);
      return;
    }
    for (let y = 0; y < size; y++)
      for (let x = 0; x < size; x++) {
        const worldX = x - size / 2 + 0.5,
          worldZ = y - size / 2 + 0.5;
        const blocked =
          Math.abs(worldX) > 30 ||
          Math.abs(worldZ) > 30 ||
          [-19, -13, 13, 19].some(
            (v) => Math.abs(worldX - v) < 1.2 && Math.abs(worldZ) > 14,
          );
        if (!blocked) cells.push({ x, y });
      }
    this.navigation = new NavigationGrid(size, size, cells);
  }
  worldToGrid(position) {
    return {
      x: Math.floor(position.x + 32),
      y: Math.floor(position.z + 32),
    };
  }
  gridToWorld(position) {
    return { x: position.x - 31.5, z: position.y - 31.5 };
  }
  canOccupy(position, radius = 0.48) {
    if (!this.navigation) return false;
    const samples = [
      [0, 0],
      [radius, 0],
      [-radius, 0],
      [0, radius],
      [0, -radius],
      [radius * 0.7, radius * 0.7],
      [-radius * 0.7, radius * 0.7],
      [radius * 0.7, -radius * 0.7],
      [-radius * 0.7, -radius * 0.7],
    ];
    return samples.every(([x, z]) => {
      const cell = this.worldToGrid({ x: position.x + x, z: position.z + z });
      return this.navigation.isWalkable(cell.x, cell.y);
    });
  }
  hasLineOfSight(from, to) {
    return this.navigation?.hasLineOfSight(
      this.worldToGrid(from),
      this.worldToGrid(to),
    );
  }
  simulationOptions() {
    return {
      canOccupy: (position, radius) => this.canOccupy(position, radius),
      hasLineOfSight: (from, to) => this.hasLineOfSight(from, to),
    };
  }
  setPath(world) {
    if (!this.runtime || this.runtime.paused) return;
    const requestToken = (this.runtime.tokens.pathRequest += 1);
    const started = performance.now(),
      path = this.navigation.findPath(
        this.worldToGrid(this.runtime.player.position),
        this.worldToGrid(world),
      );
    if (requestToken !== this.runtime.tokens.pathRequest) return;
    this.runtime.diagnostics.pathMs = performance.now() - started;
    this.runtime.player.path = path
      .slice(1)
      .map((entry) => this.gridToWorld(entry));
    this.runtime.player.destination = this.runtime.player.path.at(-1) ?? null;
  }
  cancelPath() {
    if (this.runtime) {
      this.runtime.tokens.pathRequest += 1;
      this.runtime.player.path = [];
      this.runtime.player.destination = null;
    }
  }
  movementVector() {
    const direct = this.input?.vector() ?? { x: 0, z: 0 };
    if (Math.hypot(direct.x, direct.z) > 0.05) {
      this.recordObjective("move");
      return direct;
    }
    const path = this.runtime.player.path;
    if (path?.length) {
      const next = path[0],
        dx = next.x - this.runtime.player.position.x,
        dz = next.z - this.runtime.player.position.z,
        length = Math.hypot(dx, dz);
      if (length < 0.28) {
        path.shift();
        if (!path.length) this.runtime.player.destination = null;
        return this.movementVector();
      }
      this.recordObjective("move");
      return { x: dx / length, z: dz / length };
    }
    return { x: 0, z: 0 };
  }
  loop(now) {
    if (!this.running) {
      this.animationFrame = null;
      return;
    }
    const delta = Math.min(0.1, (now - this.lastTime) / 1000 || 0);
    this.lastTime = now;
    if (
      this.runtime &&
      !this.runtime.paused &&
      this.runtime.gameState === "playing"
    ) {
      this.runtime.accumulator = Math.min(
        this.runtime.accumulator + delta,
        FIXED_STEP * 6,
      );
      let steps = 0;
      while (this.runtime.accumulator >= FIXED_STEP && steps < 6) {
        const start = performance.now();
        const events = fixedUpdate(
          this.runtime,
          this.movementVector(),
          FIXED_STEP,
          this.rng,
          this.idFactory,
          this.simulationOptions(),
        );
        this.runtime.diagnostics.physicsMs = performance.now() - start;
        this.updateCompanion(FIXED_STEP);
        this.updateSummons(FIXED_STEP);
        this.runtime.accumulator -= FIXED_STEP;
        steps += 1;
        for (const event of events) this.handleEvent(event);
      }
      this.autosaveRemaining -= delta;
      if (this.autosaveRemaining <= 0) {
        this.autosaveRemaining = 15;
        this.save();
      }
      this.audio?.update(delta);
      if (this.runtime.challenge) {
        this.runtime.challengeElapsed += delta;
        if (
          this.runtime.boss &&
          !this.runtime.boss.alive &&
          !this.runtime.challengeComplete
        )
          this.completeChallenge();
      }
    }
    this.renderer?.sync(
      this.runtime,
      this.runtime?.accumulator / FIXED_STEP || 0,
      now,
    );
    if (this.runtime) drawMinimap(this.elements.minimap, this.runtime);
    this.updateHUD();
    if (this.debug) this.updateDiagnostics();
    this.animationFrame = requestAnimationFrame((time) => this.loop(time));
  }
  updateCompanion(dt) {
    const companion = this.runtime.companion;
    if (!companion?.alive) return;
    companion.cooldown = Math.max(0, companion.cooldown - dt);
    const player = this.runtime.player;
    const enemy = this.nearestEnemy(companion.position);
    if (
      enemy &&
      distance(companion.position, enemy.position) < 10 &&
      this.hasLineOfSight(companion.position, enemy.position)
    ) {
      companion.state = "attack";
      if (companion.cooldown <= 0) {
        companion.cooldown = 1.4;
        damageEnemy(
          this.runtime,
          enemy,
          10 + this.runtime.character.level * 1.5,
          null,
          this.rng,
          `${companion.id}:attack:${(companion.attackCounter = (companion.attackCounter ?? 0) + 1)}`,
          this.idFactory,
        );
      }
    } else {
      companion.state = "follow";
      const dx = player.position.x - companion.position.x,
        dz = player.position.z - companion.position.z,
        length = Math.hypot(dx, dz);
      if (length > 16) {
        const catchUp = {
          x: player.position.x - 2,
          z: player.position.z + 1,
        };
        companion.position = this.canOccupy(catchUp)
          ? catchUp
          : { x: player.position.x, z: player.position.z };
      } else if (length > 3) {
        const next = {
          x: companion.position.x + (dx / length) * 4.5 * dt,
          z: companion.position.z + (dz / length) * 4.5 * dt,
        };
        if (this.canOccupy(next, 0.35)) companion.position = next;
      }
    }
  }
  updateSummons(dt) {
    const combatToken = this.runtime.tokens.combat;
    for (const summon of this.runtime.summons ?? []) {
      if (!summon.alive || summon.generationToken !== combatToken) continue;
      summon.lifetime -= dt;
      summon.cooldown = Math.max(0, summon.cooldown - dt);
      if (summon.lifetime <= 0 || summon.health <= 0) {
        summon.alive = false;
        summon.state = "dead";
        continue;
      }
      const enemy = this.nearestEnemy(summon.position);
      if (
        enemy &&
        distance(summon.position, enemy.position) <= 7 &&
        this.hasLineOfSight(summon.position, enemy.position)
      ) {
        summon.state = "attack";
        if (summon.cooldown <= 0) {
          summon.cooldown = 1.25;
          summon.attackCounter = (summon.attackCounter ?? 0) + 1;
          damageEnemy(
            this.runtime,
            enemy,
            7 + this.runtime.character.level,
            null,
            this.rng,
            `${summon.id}:attack:${summon.attackCounter}`,
            this.idFactory,
          );
        }
      } else {
        summon.state = "follow";
        const dx = this.runtime.player.position.x - summon.position.x;
        const dz = this.runtime.player.position.z - summon.position.z;
        const length = Math.hypot(dx, dz);
        if (length > 2.5) {
          const next = {
            x: summon.position.x + (dx / length) * 4 * dt,
            z: summon.position.z + (dz / length) * 4 * dt,
          };
          if (this.canOccupy(next, 0.3)) summon.position = next;
        }
      }
    }
    this.runtime.summons = (this.runtime.summons ?? []).filter(
      (summon) => summon.alive,
    );
  }
  handleEvent(event) {
    if (event.type === "death") {
      playerDeath(this.runtime, this.idFactory);
      this.audio.play("death");
      this.announce("You were defeated. An Echo Cache marks your lost Gold.");
      this.save();
      this.elements.death.showModal();
    }
    if (event.type === "boss-phase") {
      this.audio.play("phase");
      this.toast(`${event.name} — Phase ${event.phase}`, "#f39ba9");
      this.announce(`Boss phase ${event.phase}: ${event.name}.`);
    }
  }
  basicAttack(target) {
    if (!target?.alive) {
      this.toast("No hostile target in reach.", "#e9c96f");
      return;
    }
    const ability = CLASSES[this.runtime.character.classId].abilities[0];
    const result = executeAbility(
      this.runtime,
      ability,
      target.position,
      target,
      this.rng,
      this.idFactory,
      this.simulationOptions(),
    );
    if (!result.ok) {
      if (result.reason.includes("range")) {
        this.target = target;
        this.setPath(target.position);
      } else this.toast(result.reason, "#e9c96f");
    } else {
      this.audio.play(ability.type === "projectile" ? "cast" : "attack");
      this.target = target;
      this.recordObjective("attack", ability.id);
    }
  }
  useAbility(index, target = this.target, position = this.input?.pointerWorld) {
    if (!this.runtime) return;
    const ability = CLASSES[this.runtime.character.classId].abilities[index];
    const result = executeAbility(
      this.runtime,
      ability,
      position ??
        target?.position ?? {
          x: this.runtime.player.position.x,
          z: this.runtime.player.position.z - 1,
        },
      target?.alive ? target : null,
      this.rng,
      this.idFactory,
      this.simulationOptions(),
    );
    if (!result.ok) {
      this.toast(result.reason, "#e9c96f");
      return;
    }
    this.audio.play(
      ["projectile", "area", "ultimate", "channel"].includes(ability.type)
        ? "cast"
        : "attack",
    );
    if (ability.ultimate) this.announce(`${ability.name} ultimate unleashed.`);
  }
  dodge() {
    if (!this.runtime) return;
    const vector = this.movementVector();
    if (
      dodge(
        this.runtime,
        Math.hypot(vector.x, vector.z) > 0.1
          ? vector
          : this.runtime.player.facing,
      )
    ) {
      this.audio.play("dodge");
      this.cancelPath();
      this.recordObjective("dodge");
    }
  }
  drinkPotion() {
    if (this.runtime && usePotion(this.runtime)) {
      this.audio.play("pickup");
      this.toast("Restorative draught used.");
    } else this.toast("Potion unavailable or health already full.", "#e9c96f");
  }
  nearestEnemy(origin = this.runtime?.player.position) {
    if (!this.runtime || !origin) return null;
    return (
      this.runtime.enemies
        .filter((e) => e.alive)
        .sort(
          (a, b) => distance(origin, a.position) - distance(origin, b.position),
        )[0] ?? null
    );
  }
  cycleTarget() {
    const live = this.runtime.enemies
      .filter((e) => e.alive)
      .sort(
        (a, b) =>
          distance(this.runtime.player.position, a.position) -
          distance(this.runtime.player.position, b.position),
      );
    if (!live.length) {
      this.target = null;
      return;
    }
    const index = live.findIndex((e) => e.id === this.target?.id);
    this.target = live[(index + 1) % live.length];
  }
  nearbyInteractable() {
    const entities = this.runtime.interactables.filter((entry) => !entry.used);
    const cache = this.runtime.character.echoCache;
    if (
      cache &&
      !cache.recovered &&
      !cache.expired &&
      cache.zoneId === this.runtime.zoneId
    )
      entities.push({
        id: cache.id,
        type: "echo",
        name: "Echo Cache",
        position: cache.position,
      });
    return (
      entities
        .filter((e) => distance(this.runtime.player.position, e.position) < 2.7)
        .sort(
          (a, b) =>
            distance(this.runtime.player.position, a.position) -
            distance(this.runtime.player.position, b.position),
        )[0] ?? null
    );
  }
  interact() {
    if (!this.runtime || this.runtime.paused) return;
    const entity = this.nearbyInteractable();
    if (!entity) {
      const loot = this.runtime.groundItems.find(
        (item) => distance(this.runtime.player.position, item.position) < 2,
      );
      if (loot) this.pickup(loot.id);
      else this.toast("Nothing nearby to interact with.", "#e9c96f");
      return;
    }
    if (entity.type === "checkpoint") {
      const newlyActivated = !entity.active;
      activateCheckpoint(this.runtime, entity);
      this.audio.play("checkpoint");
      this.toast(
        newlyActivated
          ? "Waystone attuned. Respawn point saved."
          : "Waystone restored your strength.",
      );
      if (newlyActivated) {
        this.announce("Checkpoint activated.");
        this.save();
      }
    } else if (entity.type === "echo") {
      if (recoverEcho(this.runtime)) {
        this.audio.play("checkpoint");
        this.toast("Echo Cache recovered.");
        this.announce("Lost Gold recovered.");
        this.save();
      }
    } else if (entity.type === "key") {
      this.runtime.dungeonState.keys.push(entity.id);
      entity.used = true;
      this.audio.play("pickup");
      this.toast(`${entity.name} collected.`);
      this.save();
    } else if (entity.type === "door") {
      if (!this.runtime.dungeonState.keys.includes(entity.keyId)) {
        this.toast(`Requires ${entity.keyId.replaceAll("-", " ")}.`, "#e9c96f");
        return;
      }
      if (!this.runtime.dungeonState.unlockedDoors.includes(entity.id))
        this.runtime.dungeonState.unlockedDoors.push(entity.id);
      entity.used = true;
      this.buildNavigation();
      this.cancelPath();
      this.audio.play("checkpoint");
      this.toast("Sigil Door unlocked. Navigation updated.");
      this.save();
    } else if (entity.type === "portal") {
      if (this.runtime.dungeon && this.runtime.boss?.alive) {
        this.toast("The Crown-Bearer still seals the exit.", "#e9c96f");
        return;
      }
      this.travelZone(entity.destination);
    } else if (entity.type === "objective") {
      const updates = this.recordObjective(entity.objectiveType, entity.id);
      entity.used = updates.length > 0;
      if (updates.length && entity.objectiveType === "choice") {
        const ending =
          this.runtime.character.appearance.tone === "Compassionate"
            ? "Crown Released"
            : this.runtime.character.appearance.tone === "Wry"
              ? "Crown Hidden"
              : "Crown Reforged";
        if (!this.runtime.character.endings.includes(ending)) {
          this.runtime.character.endings.push(ending);
          this.runtime.character.statistics.campaignEndings.push(ending);
        }
        if (ending !== "Crown Reforged")
          unlockAchievement(this.runtime.character, "alternate-ending");
      }
      this.audio.play("quest");
      this.toast(
        updates.length
          ? "Quest objective advanced."
          : "The objective is not yet active.",
      );
    } else if (entity.type === "companion") {
      const definition = COMPANIONS.find((entry) => entry.id === entity.id);
      if (this.runtime.character.chapter < definition.unlockChapter) {
        this.toast(
          `Available in Chapter ${definition.unlockChapter}.`,
          "#e9c96f",
        );
      } else if (recruitCompanion(this.runtime.character, entity.id)) {
        this.runtime.character.activeCompanion = entity.id;
        this.setupZone(true);
        this.audio.play("quest");
        this.toast(`${definition.name} joined your journey.`);
      } else {
        this.runtime.character.activeCompanion = entity.id;
        this.setupZone(true);
        this.toast(`${definition.name} is now active.`);
      }
    } else if (entity.type === "vendor") this.openService("vendor");
    else if (entity.type === "crafting" || entity.type === "enchanter")
      this.openService("crafting");
    else if (entity.type === "stash") this.openService("stash");
    else this.openDialogue(entity);
  }
  pickup(id) {
    const result = pickupItem(this.runtime, id);
    if (result.ok) {
      this.audio.play("pickup");
      this.toast(
        `${RARITIES[result.item.rarity].name}: ${result.item.name}`,
        RARITIES[result.item.rarity].color,
      );
      this.announce(
        `${RARITIES[result.item.rarity].name} item picked up: ${result.item.name}.`,
      );
      this.save();
    } else this.toast(result.reason, "#e95f66");
  }
  recordObjective(type, id = null, amount = 1) {
    const updates = progressQuest(this.runtime.character, type, id, amount);
    if (updates.length) {
      this.runtime.questEvents.push({ type, id, time: this.runtime.time });
      const update = updates[0];
      if (update.state === "ready") {
        this.audio?.play("quest");
        this.announce(
          "Quest objectives complete. Open the Quest Journal to turn it in.",
        );
      }
    }
    return updates;
  }
  completeChallenge() {
    if (this.runtime.challengeComplete) return;
    const character = this.runtime.character;
    const challenge = this.runtime.challenge;
    this.runtime.challengeComplete = true;
    character.currencies.gold += challenge.reward.gold;
    character.currencies.challengeMarks += challenge.reward.challengeMarks;
    character.statistics.dungeonsCompleted += 1;
    character.statistics.highestChallengeTier = Math.max(
      character.statistics.highestChallengeTier,
      challenge.tier,
    );
    character.challengeRecords ??= {};
    const previous = character.challengeRecords[challenge.tier];
    if (!previous || this.runtime.challengeElapsed < previous)
      character.challengeRecords[challenge.tier] = Number(
        this.runtime.challengeElapsed.toFixed(2),
      );
    character.challengeTier = Math.min(30, character.challengeTier + 1);
    unlockAchievement(character, "challenge-first", this.debug);
    if (challenge.tier >= 10)
      unlockAchievement(character, "challenge-ten", this.debug);
    this.audio.play("victory");
    this.toast(
      `Tier ${challenge.tier} complete · ${challenge.reward.challengeMarks} Challenge Marks`,
      "#efc86d",
    );
    this.announce(
      `Challenge Dungeon tier ${challenge.tier} complete. This is a personal local record.`,
    );
    this.save();
  }
  travelZone(zoneId) {
    const region = REGIONS.find((r) => r.id === zoneId);
    if (!region) return;
    if (region.chapter > this.runtime.character.chapter) {
      this.toast("That portal has not awakened.", "#e9c96f");
      return;
    }
    this.runtime.zoneId = zoneId;
    this.runtime.tokens.zone += 1;
    this.runtime.tokens.combat += 1;
    this.runtime.tokens.pathRequest += 1;
    if (zoneId === "veilwatch" && this.runtime.challenge) {
      this.runtime.challengeSummary = {
        tier: this.runtime.challenge.tier,
        elapsed: this.runtime.challengeElapsed,
        completed: this.runtime.challengeComplete,
      };
      this.runtime.challenge = null;
      this.runtime.dungeon = null;
    }
    this.runtime.character.currentZone = zoneId;
    this.runtime.player.position = { x: 0, y: 0, z: 7 };
    this.runtime.character.position = { ...this.runtime.player.position };
    this.runtime.projectiles = [];
    this.runtime.effects = [];
    this.runtime.summons = [];
    this.input?.clear();
    this.target = null;
    if (!this.runtime.character.discoveredRegions.includes(zoneId)) {
      this.runtime.character.discoveredRegions.push(zoneId);
      this.runtime.character.statistics.regionsDiscovered += 1;
    }
    this.setupZone(false);
    this.audio.setMusic(zoneId === "veilwatch" ? "hub" : region.terrain);
    this.toast(region.name);
    this.save();
  }
  showIntroDialogue() {
    if (this.runtime.character.mode === "tutorial")
      this.openDialogue(
        { name: "Warden Ilyra", id: "ilyra" },
        "Movement first. The Veil answers courage, but it does not forgive standing still.",
      );
    else if (this.runtime.zoneId === "verdant-march")
      this.openDialogue(
        { name: "Serah, Veil Scout", id: "serah" },
        "The roots are walking again. If you hear your name from beneath the soil, keep your blade drawn.",
      );
  }
  openDialogue(entity, text = null) {
    if (!entity?.id || this.elements.dialogue.open) return;
    this.pause("Dialogue");
    this.dialogueToken = `${this.runtime.tokens.zone}:${entity.id}`;
    select(this.root, "[data-rf-dialogue-speaker]").textContent = entity.name;
    select(this.root, "[data-rf-dialogue-portrait]").textContent =
      entity.name.charAt(0);
    select(this.root, "[data-rf-dialogue-text]").textContent =
      text ??
      "The Wardens keep no easy oaths. Still, every recovered shard gives Veilwatch another dawn.";
    const choices = select(this.root, "[data-rf-dialogue-choices]");
    clear(choices);
    choices.append(
      button("Tell me about the Crown", () => {
        if (this.dialogueToken !== `${this.runtime.tokens.zone}:${entity.id}`)
          return;
        select(this.root, "[data-rf-dialogue-text]").textContent =
          "Asterra's Crown was a covenant, not a throne. The Regent wishes to make it a chain.";
      }),
      button("I am ready.", () => {
        if (this.dialogueToken !== `${this.runtime.tokens.zone}:${entity.id}`)
          return;
        this.elements.dialogue.close();
        this.dialogueToken = null;
        this.resume(false);
      }),
    );
    if (!this.elements.dialogue.open) this.elements.dialogue.showModal();
  }
  pause(reason = "Paused") {
    if (!this.runtime || this.runtime.gameState === "dead") return;
    this.runtime.paused = true;
    this.runtime.gameState = "paused";
    this.input?.clear();
    this.cancelPath();
    this.audio?.setPaused(true);
    if (
      reason !== "Dialogue" &&
      !this.elements["pause-menu"].open &&
      !this.elements["game-panel"].open
    )
      this.elements["pause-menu"].showModal();
  }
  resume(close = true) {
    if (!this.runtime?.player.alive) return;
    if (
      this.elements.dialogue.open ||
      this.elements["game-panel"].open ||
      this.elements.death.open
    )
      return;
    if (close && this.elements["pause-menu"].open)
      this.elements["pause-menu"].close();
    this.runtime.paused = false;
    this.runtime.gameState = "playing";
    this.audio?.setPaused(false);
    this.elements.canvas.focus();
  }
  escape() {
    if (this.elements["game-panel"].open) {
      this.closePanel();
      return;
    }
    if (this.elements.dialogue.open) {
      this.elements.dialogue.close();
      this.resume(false);
      return;
    }
    if (this.elements["pause-menu"].open) {
      this.resume();
      return;
    }
    this.pause();
  }
  handleRespawn() {
    if (this.elements.death.open) this.elements.death.close();
    const safe = this.navigation?.nearestReachable(
      this.worldToGrid(this.runtime.checkpoint),
      8,
    );
    if (safe) this.runtime.checkpoint = this.gridToWorld(safe);
    respawn(this.runtime);
    this.renderer.setPlayer(this.runtime.character);
    this.save();
    this.elements["pause-menu"].showModal();
    this.audio.play("checkpoint");
    this.announce("Respawned at the latest checkpoint. Resume when ready.");
  }
  async save() {
    if (!this.runtime || this.slotIndex === null) return;
    if (this.debug) {
      this.elements["save-state"].textContent =
        "Diagnostics · persistence isolated";
      return;
    }
    this.elements["save-state"].textContent = "Saving…";
    this.runtime.idCounter = this.idFactory.counter;
    const runtime = this.runtime;
    const version = this.sessionVersion;
    try {
      await this.saveManager.save(this.slotIndex, runtime);
      if (version !== this.sessionVersion || runtime !== this.runtime) return;
      this.elements["save-state"].textContent =
        `Saved ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } catch (error) {
      if (version !== this.sessionVersion || runtime !== this.runtime) return;
      this.elements["save-state"].textContent = "Save failed";
      this.toast(error.message, "#e95f66");
    }
  }
  async returnToTitle() {
    await this.save();
    if (this.elements["pause-menu"].open) this.elements["pause-menu"].close();
    this.runtime = null;
    this.sessionVersion += 1;
    this.running = false;
    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    this.renderer?.teardown({ loseContext: false });
    this.renderer = null;
    this.audio?.teardown();
    this.audio = null;
    this.input?.teardown();
    this.input = null;
    this.target = null;
    this.navigation = null;
    this.resizeObserver?.disconnect();
    this.elements.stage.hidden = true;
    this.elements.launcher.hidden = false;
    this.renderSlots();
  }
  openPanel(name) {
    if (!this.runtime) return;
    if (this.elements.dialogue.open || this.elements.death.open) return;
    if (this.elements["pause-menu"].open) this.elements["pause-menu"].close();
    this.pause("Panel");
    this.renderPanel(name);
    if (!this.elements["game-panel"].open)
      this.elements["game-panel"].showModal();
    this.audio.play("inventory");
  }
  closePanel() {
    if (this.elements["game-panel"].open) this.elements["game-panel"].close();
    this.resume(false);
  }
  renderPanel(name) {
    const titles = {
      inventory: "Inventory & Equipment",
      character: "Warden Profile",
      skills: "Passive Constellation",
      quests: "Quest Journal",
      map: "World Map",
      codex: "Bestiary & Collection",
      achievements: "Triumphs",
      settings: "Settings",
      vendor: "Merchant",
      crafting: "Forge & Enchanting",
      stash: "Warden Stash",
    };
    this.elements["panel-title"].textContent = titles[name] ?? "Warden Journal";
    selectAll(this.root, "[data-rf-panel-tab]").forEach((node) =>
      node.setAttribute(
        "aria-selected",
        String(node.dataset.rfPanelTab === name),
      ),
    );
    clear(this.elements["panel-content"]);
    const render = {
      inventory: () => this.renderInventory(),
      character: () => this.renderCharacter(),
      skills: () => this.renderSkills(),
      quests: () => this.renderQuests(),
      map: () => this.renderMap(),
      codex: () => this.renderCodex(),
      achievements: () => this.renderAchievements(),
      settings: () => this.renderSettings(),
      vendor: () => this.renderVendor(),
      crafting: () => this.renderCrafting(),
      stash: () => this.renderStash(),
    }[name];
    render?.();
  }
  renderInventory() {
    const character = this.runtime.character,
      wrap = element("div", "rf-inventory"),
      equipment = element("section", "rf-panel-card"),
      itemsSection = element("section", "rf-panel-card");
    equipment.append(element("h3", "", "Equipment"));
    const grid = element("div", "rf-equipment");
    for (const [slot, item] of Object.entries(character.equipment)) {
      const node = element("button");
      node.type = "button";
      node.style.setProperty(
        "--rarity",
        item ? RARITIES[item.rarity].color : "#759087",
      );
      node.append(
        element(
          "small",
          "",
          slot.replace(/[A-Z]/g, (m) => ` ${m}`).toUpperCase(),
        ),
        element("strong", "", item?.name ?? "Empty"),
      );
      if (item)
        node.addEventListener("click", () => {
          const result = unequipItem(character, slot);
          this.toast(
            result.ok ? `${item.name} unequipped.` : result.reason,
            result.ok ? RARITIES[item.rarity].color : "#e95f66",
          );
          if (result.ok) this.save();
          this.renderPanel("inventory");
        });
      grid.append(node);
    }
    equipment.append(
      grid,
      line("Gold", character.currencies.gold),
      line("Veil Shards", character.currencies.veilShards),
      line("Capacity", `${character.inventory.length} / ${character.capacity}`),
    );
    itemsSection.append(element("h3", "", "Carried items"));
    const items = element("div", "rf-items");
    for (const item of character.inventory) {
      const node = element("button", "rf-item");
      node.type = "button";
      node.style.setProperty("--rarity", RARITIES[item.rarity].color);
      node.append(
        element("strong", "", item.name),
        element(
          "small",
          "",
          `${RARITIES[item.rarity].name} · Lv ${item.level}`,
        ),
        element(
          "small",
          "",
          item.affixes.map((a) => `${a.name} +${a.value}`).join(" · ") ||
            "No affixes",
        ),
      );
      node.addEventListener("click", () => {
        const result = equipItem(character, item.id);
        this.toast(
          result.ok ? `${item.name} equipped.` : result.reason,
          result.ok ? RARITIES[item.rarity].color : "#e95f66",
        );
        if (result.ok) this.audio.play("inventory");
        if (result.ok) this.save();
        this.renderPanel("inventory");
      });
      node.addEventListener("contextmenu", (event) => {
        event.preventDefault();
        const result = salvageItem(character, item.id);
        if (result.ok) {
          this.toast(`${item.name} salvaged for ${result.shards} shard(s).`);
          this.renderPanel("inventory");
          this.save();
        }
      });
      items.append(node);
    }
    if (!character.inventory.length)
      items.append(
        element(
          "p",
          "",
          "Your pack is empty. Defeated foes may drop equipment.",
        ),
      );
    itemsSection.append(
      items,
      element(
        "p",
        "",
        "Select to equip. Right-click an item to salvage it atomically.",
      ),
    );
    wrap.append(equipment, itemsSection);
    this.elements["panel-content"].append(wrap);
  }
  renderCharacter() {
    const character = this.runtime.character,
      stats = derivedStats(character),
      grid = element("div", "rf-panel-grid"),
      identity = element("section", "rf-panel-card"),
      attributes = element("section", "rf-panel-card"),
      progress = element("section", "rf-panel-card");
    identity.append(
      element("h3", "", character.name),
      line("Class", CLASSES[character.classId].name),
      line("Level", character.level),
      line("Chapter", character.chapter),
      line(
        "Location",
        REGIONS.find((r) => r.id === character.currentZone)?.name,
      ),
      line(
        "Companion",
        COMPANIONS.find((c) => c.id === character.activeCompanion)?.name ??
          "None",
      ),
    );
    attributes.append(
      element("h3", "", "Derived statistics"),
      line("Health", Math.round(stats.health)),
      line("Power", Math.round(stats.power)),
      line("Armor", Math.round(stats.armor)),
      line("Movement", stats.speed.toFixed(1)),
      line("Critical", `${Math.round(stats.crit * 100)}%`),
      line("Attribute points", character.attributePoints),
    );
    for (const key of ["power", "vitality", "guard"]) {
      const add = button(`Raise ${key}`, () => {
        if (character.attributePoints <= 0) {
          this.toast("No attribute points available.", "#e9c96f");
          return;
        }
        character.attributes[key] += 1;
        character.attributePoints -= 1;
        this.save();
        this.renderPanel("character");
      });
      attributes.append(add);
    }
    progress.append(
      element("h3", "", "Progress"),
      line("XP", `${character.xp} / ${100 + (character.level - 1) * 55}`),
      line("Quests", character.statistics.questsCompleted),
      line("Enemies", character.statistics.enemiesDefeated),
      line("Bosses", character.statistics.bossesDefeated),
      line(
        "Play time",
        this.formatTime(character.playTime + this.runtime.time),
      ),
    );
    grid.append(identity, attributes, progress);
    this.elements["panel-content"].append(grid);
  }
  renderSkills() {
    const character = this.runtime.character;
    this.elements["panel-content"].append(
      element(
        "p",
        "",
        `${character.skillPoints} skill point(s) available. Prerequisites and maximum ranks are enforced.`,
      ),
    );
    const tree = element("div", "rf-skill-tree");
    for (const node of SKILL_TREES[character.classId]) {
      const rank = character.skills[node.id] ?? 0,
        control = element("button", "rf-skill-node");
      control.type = "button";
      control.dataset.unlocked = String(rank > 0);
      control.append(
        element("strong", "", node.name),
        element("small", "", node.description),
        element("small", "", `Rank ${rank} / ${node.max}`),
      );
      control.addEventListener("click", () => {
        const result = spendSkillPoint(character, node.id);
        this.toast(
          result.ok ? `${node.name} rank ${result.rank}.` : result.reason,
          result.ok ? "#e9c96f" : "#e95f66",
        );
        if (result.ok) this.save();
        this.renderPanel("skills");
      });
      tree.append(control);
    }
    this.elements["panel-content"].append(tree);
  }
  renderQuests() {
    const grid = element("div", "rf-panel-grid");
    for (const definition of QUESTS) {
      const state = this.runtime.character.quests[definition.id],
        card = element("section", "rf-panel-card");
      card.append(
        element("h3", "", definition.title),
        line("Type", definition.type),
        line("State", state?.state ?? "unavailable"),
      );
      if (state?.state === "active") {
        const objective = definition.objectives[state.objective];
        card.append(
          element("p", "", objective?.text ?? "Return for your reward."),
          line("Progress", `${state.progress} / ${objective?.target ?? 1}`),
        );
      }
      if (state?.state === "ready")
        card.append(
          button("Complete quest", () => {
            const result = completeQuest(this.runtime.character, definition.id);
            if (result.ok) {
              this.audio.play("quest");
              this.toast(`Quest complete · ${result.gold} Gold`, "#e9c96f");
              this.renderPanel("quests");
              this.save();
            }
          }),
        );
      grid.append(card);
    }
    this.elements["panel-content"].append(grid);
  }
  renderMap() {
    const map = element("div", "rf-map-canvas");
    for (const region of REGIONS) {
      const control = element("button", "rf-map-region", region.name);
      control.type = "button";
      control.style.left = `${region.position[0]}%`;
      control.style.top = `${region.position[1]}%`;
      control.style.setProperty("--region-color", region.color);
      control.disabled = region.chapter > this.runtime.character.chapter;
      control.title = region.description;
      control.addEventListener("click", () => {
        this.elements["game-panel"].close();
        this.travelZone(region.id);
        this.resume(false);
      });
      map.append(control);
    }
    this.elements["panel-content"].append(
      map,
      element(
        "p",
        "",
        `Discovered ${this.runtime.character.discoveredRegions.length} of ${REGIONS.length} regions. Markers show portals, checkpoints, bosses, vendors, and your Echo Cache.`,
      ),
    );
  }
  renderCodex() {
    const grid = element("div", "rf-panel-grid"),
      bestiary = element("section", "rf-panel-card"),
      library = element("section", "rf-panel-card"),
      companions = element("section", "rf-panel-card");
    bestiary.append(element("h3", "", "Bestiary"));
    for (const enemy of ENEMIES)
      bestiary.append(
        line(
          enemy.name,
          this.runtime.character.bestiary[enemy.id] ?? "Undiscovered",
        ),
      );
    library.append(element("h3", "", "Item collection"));
    if (!this.runtime.character.itemLibrary.length)
      library.append(element("p", "", "No item silhouettes recorded."));
    else
      this.runtime.character.itemLibrary.forEach((name) =>
        library.append(line(name, "Found")),
      );
    companions.append(element("h3", "", "Companions"));
    for (const companion of COMPANIONS) {
      const recruited = this.runtime.character.recruitedCompanions.includes(
        companion.id,
      );
      companions.append(
        line(
          companion.name,
          recruited ? "Recruited" : `Chapter ${companion.unlockChapter}`,
        ),
      );
      if (recruited)
        companions.append(
          button(
            this.runtime.character.activeCompanion === companion.id
              ? "Active"
              : `Travel with ${companion.name.split(",")[0]}`,
            () => {
              this.runtime.character.activeCompanion = companion.id;
              this.setupZone(true);
              this.renderPanel("codex");
            },
          ),
        );
    }
    grid.append(bestiary, library, companions);
    this.elements["panel-content"].append(grid);
  }
  renderAchievements() {
    const grid = element("div", "rf-panel-grid");
    for (const achievement of ACHIEVEMENTS) {
      const unlocked = this.runtime.character.achievements.includes(
          achievement.id,
        ),
        card = element("section", "rf-panel-card");
      card.append(
        element(
          "h3",
          "",
          unlocked ? `✦ ${achievement.name}` : `◇ ${achievement.name}`,
        ),
        element("p", "", achievement.description),
        line("Status", unlocked ? "Unlocked" : "In progress"),
      );
      grid.append(card);
    }
    this.elements["panel-content"].append(grid);
  }
  renderSettings() {
    const settings = this.saveManager.settings,
      grid = element("div", "rf-setting-grid");
    const selectSetting = (label, key, options) => {
      const wrap = element("label", "rf-setting", label),
        control = document.createElement("select");
      for (const [value, name] of options) {
        const option = element("option", "", name);
        option.value = value;
        option.selected = settings[key] === value;
        control.append(option);
      }
      control.addEventListener("change", () => {
        settings[key] = control.value;
        this.saveManager.saveSettings(settings);
        if (key === "quality") this.renderer.setQuality(control.value);
      });
      wrap.append(control);
      return wrap;
    };
    grid.append(
      selectSetting("Graphics quality", "quality", [
        ["low", "Low"],
        ["medium", "Medium"],
        ["high", "High"],
        ["ultra", "Ultra"],
      ]),
    );
    for (const [label, key] of [
      ["Master volume", "masterVolume"],
      ["Music volume", "musicVolume"],
      ["Effects volume", "effectsVolume"],
    ]) {
      const wrap = element("label", "rf-setting", label),
        control = document.createElement("input");
      control.type = "range";
      control.min = "0";
      control.max = "1";
      control.step = "0.05";
      control.value = settings[key];
      control.addEventListener("input", () => {
        settings[key] = Number(control.value);
        this.saveManager.saveSettings(settings);
        this.audio.applySettings(settings);
      });
      wrap.append(control);
      grid.append(wrap);
    }
    for (const [label, key] of [
      ["Reduced motion", "reducedMotion"],
      ["Disable screen shake", "screenShake"],
      ["Reduce flashes", "reduceFlashes"],
      ["Fixed camera orientation", "fixedCamera"],
      ["Larger cursor", "largeCursor"],
      ["Larger HUD", "largeHud"],
      ["High-contrast outlines", "highContrast"],
    ]) {
      const wrap = element("label", "rf-setting rf-setting-check"),
        control = document.createElement("input");
      control.type = "checkbox";
      control.checked =
        key === "screenShake" ? !settings.screenShake : settings[key];
      control.addEventListener("change", () => {
        settings[key] =
          key === "screenShake" ? !control.checked : control.checked;
        this.saveManager.saveSettings(settings);
        this.applySettings();
      });
      wrap.append(control, document.createTextNode(label));
      grid.append(wrap);
    }
    this.elements["panel-content"].append(
      grid,
      element(
        "p",
        "",
        `Settings use localStorage key realmfallSettings. Character progression uses IndexedDB schema ${SAVE_SCHEMA_VERSION}.`,
      ),
    );
  }
  openService(name) {
    this.openPanel(name);
  }
  renderVendor() {
    const character = this.runtime.character,
      vendor = VENDORS[0],
      card = element("section", "rf-panel-card"),
      items = element("div", "rf-items");
    card.append(
      element("h3", "", vendor.name),
      element(
        "p",
        "",
        `${vendor.role}. Inventory refreshes deterministically when you enter Veilwatch. Purchases transfer once and fail safely when your pack is full.`,
      ),
    );
    this.runtime.vendorStocks ??= {};
    if (!this.runtime.vendorStocks[vendor.id]) {
      const rng = new SeededRandom(
        `${this.runtime.seed}:vendor:${vendor.id}:${character.level}`,
      );
      this.runtime.vendorStocks[vendor.id] = Array.from(
        { length: 6 },
        (_, index) => {
          const item = generateItem(
            null,
            character.level,
            index === 5 ? "rare" : null,
            rng,
            this.idFactory,
          );
          item.owner = "vendor";
          return item;
        },
      );
    }
    const stock = this.runtime.vendorStocks[vendor.id];
    for (const item of stock) {
      const control = element("button", "rf-item");
      control.type = "button";
      control.style.setProperty("--rarity", RARITIES[item.rarity].color);
      control.append(
        element("strong", "", item.name),
        element(
          "small",
          "",
          `${item.value} Gold · ${RARITIES[item.rarity].name}`,
        ),
      );
      control.addEventListener("click", () => {
        control.disabled = true;
        const result = buyVendorItem(character, stock, item.id);
        this.toast(
          result.ok ? `${item.name} purchased.` : result.reason,
          result.ok ? RARITIES[item.rarity].color : "#e95f66",
        );
        if (result.ok) {
          this.audio.play("vendor");
          this.save();
        }
        this.renderPanel("vendor");
      });
      items.append(control);
    }
    if (!stock.length)
      items.append(element("p", "", "Sold out until the next expedition."));
    const sell = element("div", "rf-items");
    for (const item of character.inventory) {
      const control = button(`Sell ${item.name}`, () => {
        control.disabled = true;
        const result = sellInventoryItem(character, item.id);
        this.toast(
          result.ok
            ? `${item.name} sold for ${result.gold} Gold.`
            : result.reason,
          result.ok ? "#efc86d" : "#e95f66",
        );
        if (result.ok) this.save();
        this.renderPanel("vendor");
      });
      control.className = "rf-item";
      sell.append(control);
    }
    card.append(items, element("h3", "", "Sell carried items"), sell);
    this.elements["panel-content"].append(card);
  }
  renderCrafting() {
    const card = element("section", "rf-panel-card"),
      grid = element("div", "rf-panel-grid");
    card.append(
      element("h3", "", "Brannoc's Veilforge"),
      element(
        "p",
        "",
        "Atomic recipes deduct resources and apply their result once.",
      ),
    );
    for (const [operation, title] of [
      ["potion", "Craft potion · 20 Gold"],
      ["key", "Forge dungeon key · 50 Gold + 3 Shards"],
      ["dye", "Mix cosmetic dye · 35 Gold + 2 Shards"],
    ])
      grid.append(
        button(title, (event) => {
          event.currentTarget.disabled = true;
          const result = applyCrafting(this.runtime.character, operation);
          this.toast(
            result.ok ? `${title.split(" ·")[0]} complete.` : result.reason,
            result.ok ? "#7ce5bd" : "#e95f66",
          );
          if (result.ok) this.audio.play("crafting");
          if (result.ok) this.save();
          this.renderPanel("crafting");
        }),
      );
    card.append(grid);
    if (this.runtime.character.inventory.length) {
      const improve = element("div", "rf-items");
      for (const item of this.runtime.character.inventory) {
        const control = button(`Improve ${item.name}`, () => {
          control.disabled = true;
          const result = applyCrafting(
            this.runtime.character,
            "improve",
            item.id,
          );
          this.toast(
            result.ok ? `${item.name} improved.` : result.reason,
            result.ok ? RARITIES[item.rarity].color : "#e95f66",
          );
          if (result.ok) this.save();
          this.renderPanel("crafting");
        });
        control.className = "rf-item";
        control.style.setProperty("--rarity", RARITIES[item.rarity].color);
        improve.append(control);
      }
      card.append(element("h3", "", "Equipment improvement"), improve);
    }
    this.elements["panel-content"].append(card);
  }
  renderStash() {
    const character = this.runtime.character,
      wrap = element("div", "rf-inventory"),
      pack = element("section", "rf-panel-card"),
      stash = element("section", "rf-panel-card");
    pack.append(element("h3", "", "Inventory"));
    for (const item of character.inventory)
      pack.append(
        button(
          item.name,
          () => {
            const result = transferItem(
              character,
              item.id,
              "inventory",
              "stash",
            );
            if (!result.ok) this.toast(result.reason, "#e95f66");
            else this.save();
            this.renderPanel("stash");
          },
          "rf-item",
        ),
      );
    stash.append(element("h3", "", "Account stash"));
    for (const item of character.stash)
      stash.append(
        button(
          item.name,
          () => {
            const result = transferItem(
              character,
              item.id,
              "stash",
              "inventory",
            );
            if (!result.ok) this.toast(result.reason, "#e95f66");
            else this.save();
            this.renderPanel("stash");
          },
          "rf-item",
        ),
      );
    wrap.append(pack, stash);
    this.elements["panel-content"].append(wrap);
  }
  renderAbilityBar() {
    clear(this.elements["ability-bar"]);
    for (const [index, ability] of CLASSES[
      this.runtime.character.classId
    ].abilities.entries()) {
      const control = element("button", "rf-ability");
      control.type = "button";
      control.title = `${ability.name}: ${ability.cost} ${CLASSES[this.runtime.character.classId].resource}, ${ability.cooldown}s cooldown`;
      const image = document.createElement("img");
      image.src = `${this.root.dataset.assets}ability-${ability.id}.png`;
      image.alt = "";
      const key = element("kbd", "", String(index + 1)),
        cost = element(
          "small",
          "",
          ability.cost ? String(ability.cost) : "Basic",
        ),
        cooldown = element("span", "rf-cooldown", "");
      control.append(image, key, cost, cooldown);
      control.addEventListener("click", () => this.useAbility(index));
      control.dataset.abilityId = ability.id;
      this.elements["ability-bar"].append(control);
    }
  }
  updateHUD() {
    const runtime = this.runtime;
    if (!runtime) return;
    const player = runtime.player,
      character = runtime.character,
      klass = CLASSES[character.classId],
      stats = derivedStats(character);
    this.elements.health.textContent = `${Math.ceil(player.health)} / ${Math.ceil(player.maxHealth)}`;
    this.elements["health-fill"].style.setProperty(
      "--empty",
      `${100 - (player.health / player.maxHealth) * 100}%`,
    );
    this.elements.resource.textContent = `${Math.floor(player.resource)} / ${player.maxResource}`;
    this.elements["resource-fill"].style.setProperty(
      "--empty",
      `${100 - (player.resource / player.maxResource) * 100}%`,
    );
    this.elements["resource-name"].textContent = klass.resource;
    this.elements["player-name"].textContent = character.name;
    this.elements["player-class"].textContent =
      `${klass.name} · Level ${character.level}`;
    const xpRequired = 100 + (character.level - 1) * 55;
    this.elements.xp.textContent = `${character.xp} / ${xpRequired} XP`;
    this.elements["xp-fill"].style.width =
      `${Math.min(100, (character.xp / xpRequired) * 100)}%`;
    const region = REGIONS.find((r) => r.id === runtime.zoneId);
    this.elements.zone.textContent = region?.name ?? "Asterra";
    this.elements.chapter.textContent = character.chapter
      ? `Chapter ${this.roman(character.chapter)}`
      : "Tutorial";
    const questEntry = Object.entries(character.quests).find(
        ([, state]) => state.state === "active",
      ),
      quest = QUESTS.find((q) => q.id === questEntry?.[0]),
      state = questEntry?.[1],
      objective = quest?.objectives[state?.objective];
    if (runtime.challenge) {
      const modifierNames = runtime.challenge.modifiers
        .filter((modifier) =>
          CHALLENGE_MODIFIERS.some((entry) => entry.id === modifier.id),
        )
        .map((modifier) => modifier.name)
        .join(" · ");
      this.elements["quest-title"].textContent =
        `Challenge Tier ${runtime.challenge.tier}`;
      this.elements["quest-objective"].textContent =
        `${this.formatTime(runtime.challengeElapsed)} · ${modifierNames}`;
      this.elements.objective.textContent = "Defeat the Crown-Bearer";
      this.elements["quest-progress"].style.width =
        `${runtime.boss ? Math.max(0, (runtime.boss.health / runtime.boss.maxHealth) * 100) : 0}%`;
    } else {
      this.elements["quest-title"].textContent =
        quest?.title ?? "Explore Asterra";
      this.elements["quest-objective"].textContent =
        objective?.text ?? "Return to Veilwatch";
      this.elements.objective.textContent =
        objective?.text ?? "Choose your next path";
      this.elements["quest-progress"].style.width =
        `${objective ? Math.min(100, (state.progress / objective.target) * 100) : 100}%`;
    }
    if (this.target && !this.target.alive) this.target = null;
    this.elements["target-card"].hidden = !this.target;
    if (this.target) {
      this.elements["target-name"].textContent = this.target.name;
      this.elements["target-rank"].textContent = this.target.bossId
        ? "CROWN-BEARER"
        : this.target.elite
          ? `ELITE · ${this.target.elite.name}`
          : this.target.faction;
      this.elements["target-health"].style.transform =
        `scaleX(${this.target.health / this.target.maxHealth})`;
    }
    const boss = runtime.boss?.alive ? runtime.boss : null;
    this.elements["boss-hud"].hidden = !boss;
    if (boss) {
      const definition = BOSSES.find((b) => b.id === boss.bossId);
      this.elements["boss-name"].textContent = boss.name;
      this.elements["boss-title"].textContent = "CROWN-BEARER";
      this.elements["boss-health"].style.transform =
        `scaleX(${boss.health / boss.maxHealth})`;
      this.elements["boss-phase"].textContent =
        definition?.phases[boss.phase - 1] ?? `Phase ${boss.phase}`;
    }
    for (const control of selectAll(
      this.elements["ability-bar"],
      "[data-ability-id]",
    )) {
      const value = player.cooldowns[control.dataset.abilityId] ?? 0,
        cooldown = select(control, ".rf-cooldown");
      cooldown.textContent = value > 0 ? value.toFixed(1) : "";
      cooldown.style.setProperty("--cooldown", `${value > 0 ? 0 : 100}%`);
      control.disabled = !canUseAbility(
        runtime,
        klass.abilities.find((a) => a.id === control.dataset.abilityId),
        null,
        this.simulationOptions(),
      ).ok;
      control.setAttribute(
        "aria-label",
        `${klass.abilities.find((a) => a.id === control.dataset.abilityId)?.name}; ${value > 0 ? `${value.toFixed(1)} seconds cooldown` : "ready"}`,
      );
    }
    clear(this.elements.statuses);
    for (const status of player.statuses) {
      const definition = STATUS_DEFINITIONS[status.id],
        node = element(
          "span",
          "rf-status",
          `${definition.icon}${Math.ceil(status.remaining)}`,
        );
      node.title = `${definition.name}, ${Math.ceil(status.remaining)} seconds`;
      node.setAttribute(
        "aria-label",
        `${definition.name}, ${status.stacks} stack${status.stacks === 1 ? "" : "s"}, ${Math.ceil(status.remaining)} seconds`,
      );
      node.style.setProperty("--status-color", definition.color);
      this.elements.statuses.append(node);
    }
    const nearby = this.nearbyInteractable();
    this.elements.interact.hidden = !nearby;
    if (nearby)
      select(this.elements.interact, "span").textContent = nearby.name;
    this.renderLootLabels();
  }
  renderLootLabels() {
    clear(this.elements["loot-layer"]);
    if (!this.renderer) return;
    const rect = this.elements.canvas.getBoundingClientRect();
    for (const item of this.runtime.groundItems.slice(-12)) {
      const vector = { x: item.position.x, y: 0.5, z: item.position.z };
      const projected = new this.renderer.camera.position.constructor(
        vector.x,
        vector.y,
        vector.z,
      ).project(this.renderer.camera);
      if (projected.z > 1) continue;
      const node = element("button", "rf-loot-label", item.name);
      node.type = "button";
      node.style.left = `${(projected.x * 0.5 + 0.5) * rect.width}px`;
      node.style.top = `${(-projected.y * 0.5 + 0.5) * rect.height}px`;
      node.style.setProperty("--loot-color", RARITIES[item.rarity].color);
      node.addEventListener("click", () => this.pickup(item.id));
      this.elements["loot-layer"].append(node);
    }
  }
  applySettings() {
    const settings = this.saveManager.settings;
    this.root.dataset.largeHud = String(settings.largeHud);
    this.root.dataset.largeCursor = String(settings.largeCursor);
    this.root.dataset.highContrast = String(settings.highContrast);
    this.root.dataset.reduceFlashes = String(settings.reduceFlashes);
    if (this.renderer) {
      this.renderer.settings = settings;
      this.renderer.setQuality(settings.quality);
    }
    this.audio?.applySettings(settings);
  }
  updateDiagnostics() {
    if (!this.runtime) return;
    const render = this.renderer?.diagnostics() ?? {},
      ownership = validateItemOwnership(
        this.runtime.character,
        this.runtime.groundItems,
        this.runtime.vendorStocks,
      );
    const nearestRoom = this.runtime.dungeon?.rooms
      ?.map((room) => ({
        room,
        position: this.dungeonWorld({
          x: room.x + room.w / 2,
          y: room.y + room.h / 2,
        }),
      }))
      .sort(
        (a, b) =>
          distance(this.runtime.player.position, a.position) -
          distance(this.runtime.player.position, b.position),
      )[0]?.room;
    const lines = {
      gameState: this.runtime.gameState,
      generationToken: this.runtime.generationToken,
      worldSeed: this.runtime.seed,
      dungeonSeed: this.runtime.dungeon?.seed ?? "none",
      zone: this.runtime.zoneId,
      room: nearestRoom?.id ?? nearestRoom?.type ?? "outdoors",
      player: `${this.runtime.player.position.x.toFixed(2)}, ${this.runtime.player.position.z.toFixed(2)}`,
      velocity: `${this.runtime.player.velocity.x.toFixed(2)}, ${this.runtime.player.velocity.z.toFixed(2)}`,
      pathNodes: this.runtime.player.path.length,
      navigationPath:
        this.runtime.player.path
          .slice(0, 8)
          .map((entry) => `${entry.x.toFixed(1)},${entry.z.toFixed(1)}`)
          .join(" > ") || "none",
      destination: this.runtime.player.destination
        ? `${this.runtime.player.destination.x.toFixed(2)}, ${this.runtime.player.destination.z.toFixed(2)}`
        : "none",
      collisionCapsule: "r .48 / h 1.72",
      physicsBodies:
        1 +
        this.runtime.enemies.filter((entry) => entry.alive).length +
        (this.runtime.companion?.alive ? 1 : 0) +
        (this.runtime.summons?.length ?? 0),
      colliders: this.navigation?.walkable.size ?? 0,
      physicsStep: FIXED_STEP,
      playerClass: this.runtime.character.classId,
      health: `${this.runtime.player.health.toFixed(1)} / ${this.runtime.player.maxHealth}`,
      resource: this.runtime.player.resource.toFixed(1),
      cooldowns: JSON.stringify(this.runtime.player.cooldowns),
      statuses:
        this.runtime.player.statuses.map((s) => s.id).join(",") || "none",
      target: this.target?.name ?? "none",
      enemyAI: this.runtime.enemies
        .filter((e) => e.alive)
        .reduce(
          (acc, e) => ((acc[e.state] = (acc[e.state] ?? 0) + 1), acc),
          {},
        ),
      companion: this.runtime.companion?.state ?? "none",
      summons: this.runtime.summons?.length ?? 0,
      bossPhase: this.runtime.boss?.phase ?? "none",
      activeAttacks: this.runtime.enemies.filter(
        (entry) => entry.alive && ["attack", "cast"].includes(entry.state),
      ).length,
      projectiles: this.runtime.projectiles.length,
      particles: this.runtime.effects.length,
      corpses: this.runtime.corpses.length,
      loot: this.runtime.groundItems.length,
      pathfindingQueue: 0,
      entities:
        this.runtime.enemies.length + this.runtime.projectiles.length + 1,
      drawCalls: render.drawCalls,
      triangles: render.triangles,
      textures: render.textures,
      geometries: render.geometries,
      renderTargets: render.renderTargets,
      programs: render.programs,
      rendererMemory: `${render.geometries ?? 0} geometries / ${render.textures ?? 0} textures / ${render.renderTargets ?? 0} targets`,
      FPS: render.fps,
      frameTimeMs: (render.frameTimeMs ?? 0).toFixed(2),
      simulationTime: this.runtime.time.toFixed(2),
      physicsMs: this.runtime.diagnostics.physicsMs.toFixed(2),
      aiMs: (this.runtime.diagnostics.aiMs ?? 0).toFixed(2),
      pathMs: this.runtime.diagnostics.pathMs.toFixed(2),
      animation: this.runtime.player.animation,
      loopActive: this.running && this.animationFrame !== null,
      physicsWorldActive: Boolean(this.navigation),
      sessionToken: this.runtime.tokens.session,
      zoneToken: this.runtime.tokens.zone,
      combatToken: this.runtime.tokens.combat,
      playerLifeToken: this.runtime.tokens.playerLife,
      bossPhaseToken: this.runtime.tokens.bossPhase,
      pathRequestToken: this.runtime.tokens.pathRequest,
      saveSchema: SAVE_SCHEMA_VERSION,
      assetManifestVersion: this.manifest?.schemaVersion ?? "not loaded",
      loadedAssets: this.manifest?.assets?.length ?? 0,
      audioContext: this.audio?.context?.state ?? "not started",
      quality: this.saveManager.settings.quality,
      WebGL2: render.WebGL2,
      contextLost: render.contextLost,
      maxTextureSize: render.maxTextureSize,
      itemOwnership: ownership.valid ? `valid (${ownership.count})` : "INVALID",
    };
    this.elements["debug-output"].textContent = Object.entries(lines)
      .map(
        ([key, value]) =>
          `${key}: ${typeof value === "object" ? JSON.stringify(value) : value}`,
      )
      .join("\n");
  }
  debugAction(action) {
    if (!this.runtime) return;
    if (action === "enemy")
      spawnEnemy(
        this.runtime,
        "hollow-soldier",
        {
          x: this.runtime.player.position.x + 4,
          z: this.runtime.player.position.z,
        },
        { elite: true },
        this.idFactory,
      );
    if (action === "boss") this.spawnRegionBoss();
    if (action === "level") grantExperience(this.runtime.character, 500);
    if (action === "item") {
      const item = generateItem(
        null,
        this.runtime.character.level,
        "legendary",
        this.rng,
        this.idFactory,
      );
      item.owner = "inventory";
      this.runtime.character.inventory.push(item);
    }
    if (action === "death") {
      this.runtime.player.health = 0;
    }
    if (action === "checkpoint") {
      this.runtime.player.position = { ...this.runtime.checkpoint, y: 0 };
    }
    if (action === "save") this.save();
    if (action === "validate") {
      const dungeon = generateDungeon(`${this.runtime.seed}:debug`);
      this.toast(
        validateDungeon(dungeon).valid
          ? "Dungeon and ownership invariants valid."
          : "Validation failed.",
        validateDungeon(dungeon).valid ? "#7ce5bd" : "#e95f66",
      );
    }
    this.toast(
      `Diagnostics action: ${action}. Persistent rewards and achievements suppressed.`,
      "#8fd9f0",
    );
  }
  async toggleFullscreen() {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await this.root.requestFullscreen();
    } catch (_error) {
      this.toast("Fullscreen is unavailable.", "#e9c96f");
    }
  }
  formatTime(seconds) {
    const total = Math.max(0, Math.floor(seconds));
    return `${Math.floor(total / 3600)}h ${Math.floor((total % 3600) / 60)}m`;
  }
  roman(number) {
    return ["", "I", "II", "III", "IV", "V"][number] ?? String(number);
  }
  toast(message, color = "#7ce5bd") {
    const node = element("div", "rf-toast", message);
    node.style.setProperty("--toast-color", color);
    this.elements.toasts.append(node);
    const timer = setTimeout(() => {
      node.remove();
      this.toastTimers.delete(timer);
    }, 3200);
    this.toastTimers.add(timer);
  }
  announce(message) {
    if (this.announceFrame !== null) cancelAnimationFrame(this.announceFrame);
    this.elements.live.textContent = "";
    const version = this.sessionVersion;
    this.announceFrame = requestAnimationFrame(() => {
      this.announceFrame = null;
      if (version !== this.sessionVersion || this.controller.signal.aborted)
        return;
      this.elements.live.textContent = message;
    });
  }
  teardown() {
    this.running = false;
    if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
    this.controller.abort();
    for (const timer of this.toastTimers) clearTimeout(timer);
    this.toastTimers.clear();
    if (this.announceFrame !== null) cancelAnimationFrame(this.announceFrame);
    this.announceFrame = null;
    this.resizeObserver?.disconnect();
    this.input?.teardown();
    this.audio?.teardown();
    this.renderer?.teardown();
    this.saveManager.teardown();
    this.runtime = null;
    if (this.root.realmfallGame === this) delete this.root.realmfallGame;
  }
}

const root = document.querySelector("[data-realmfall-app]");
if (root) {
  const game = new RealmfallGame(root);
  if (game.debug) root.realmfallGame = game;
  game.initialize().catch((error) => {
    const live = select(root, "[data-rf-live]");
    if (live)
      live.textContent = `Realmfall could not initialize: ${error.message}`;
  });
}

export {
  RealmfallGame,
  InputController,
  challengeDefinition,
  generateDungeon,
  validateDungeon,
};
